import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { sendChatMessage, getChatHistory, clearChatHistory } from '@/services/leadChatService';
import type { ChatMessage, ChatContextData } from '@/types/leadChat';
import type { Lead } from '@/types/lead';
import { useToast } from '@/hooks/use-toast';

// Helper to get user-friendly error messages
const getErrorMessage = (error: string): string => {
  if (error.includes('rate limit')) {
    return 'Too many requests. Please wait a moment and try again.';
  }
  if (error.includes('credits exhausted') || error.includes('402')) {
    return 'OpenAI credits exhausted. Please contact your administrator.';
  }
  if (error.includes('Unauthorized') || error.includes('401')) {
    return 'Your session has expired. Please refresh the page and log in again.';
  }
  if (error.includes('Lead not found') || error.includes('404')) {
    return 'This lead could not be found in the database.';
  }
  if (error.includes('Access denied') || error.includes('different company') || error.includes('403')) {
    return 'You don\'t have permission to access this lead. It may belong to a different company.';
  }
  return error || 'An unexpected error occurred. Please try again.';
};

export const useLeadChatbot = (lead: Lead) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isContextLoading, setIsContextLoading] = useState(false);
  const [contextData, setContextData] = useState<ChatContextData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hasLoadedContext, setHasLoadedContext] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Load chat history
  const { data: chatHistory } = useQuery({
    queryKey: ['leadChatHistory', lead.id],
    queryFn: () => getChatHistory(lead.id),
    enabled: isOpen,
  });

  // Update messages when chat history loads
  useEffect(() => {
    if (chatHistory) {
      setMessages(chatHistory);
    }
  }, [chatHistory]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load context when chat first opens
  useEffect(() => {
    if (isOpen && !hasLoadedContext) {
      loadContext();
    }
  }, [isOpen, hasLoadedContext]);

  const loadContext = async () => {
    setIsContextLoading(true);
    setError(null);
    try {
      // Verify we have a valid lead
      if (!lead || !lead.id) {
        throw new Error('Invalid lead data');
      }

      // Build context data from lead
      const context: ChatContextData = {
        leadName: lead.name,
        website: lead.website || undefined,
        category: lead.category || undefined,
        status: lead.status || undefined,
        hasWebSearchData: !!lead.webSearchData,
        hasNotes: (lead.notes?.length || 0) > 0,
        hasTasks: false, // Will be shown in backend context
        hasContacts: (lead.keyContacts?.length || 0) > 0,
        notesCount: lead.notes?.length || 0,
        tasksCount: 0, // Will be shown in backend context
        contactsCount: lead.keyContacts?.length || 0,
      };

      setContextData(context);
      setHasLoadedContext(true);
    } catch (err) {
      console.error('Error loading context:', err);
      const errorMsg = err instanceof Error ? err.message : 'Failed to load lead context';
      setError(errorMsg);
      toast({
        variant: 'destructive',
        title: 'Context Load Failed',
        description: errorMsg,
      });
    } finally {
      setIsContextLoading(false);
    }
  };

  const sendMessage = async (message: string, performWebSearch?: boolean) => {
    if (!message.trim() || isLoading) return;

    setError(null);
    setIsLoading(true);

    // Optimistically add user message
    const tempUserMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      leadId: lead.id,
      userId: '', // Will be set by backend
      companyId: '', // Will be set by backend
      role: 'user',
      content: message,
      metadata: {},
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setMessages(prev => [...prev, tempUserMessage]);

    try {
      const response = await sendChatMessage({
        leadId: lead.id,
        message,
        performWebSearch,
      });

      if (response.success && response.message) {
        // Remove temp message and add both user and AI messages
        setMessages(prev => {
          const withoutTemp = prev.filter(m => m.id !== tempUserMessage.id);
          const newMessages = [];
          
          // Add user message if returned
          if (response.userMessage) {
            newMessages.push(response.userMessage);
          }
          
          // Add AI message
          newMessages.push(response.message);
          
          return [...withoutTemp, ...newMessages];
        });

        // Invalidate cache to refresh history
        queryClient.invalidateQueries({ queryKey: ['leadChatHistory', lead.id] });
      } else {
        // Remove temp message and show error
        setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
        const errorMsg = getErrorMessage(response.error || '');
        setError(errorMsg);
        toast({
          variant: 'destructive',
          title: 'Failed to send message',
          description: errorMsg,
        });
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('An unexpected error occurred');
      // Remove temp message on error
      setMessages(prev => prev.filter(m => m.id !== tempUserMessage.id));
      
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'An unexpected error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleChatOpen = () => {
    setIsOpen(prev => !prev);
  };

  const clearError = () => {
    setError(null);
  };

  const clearConversation = async () => {
    try {
      const success = await clearChatHistory(lead.id);
      if (success) {
        setMessages([]);
        queryClient.invalidateQueries({ queryKey: ['leadChatHistory', lead.id] });
        toast({
          title: 'Conversation cleared',
          description: 'Chat history has been deleted',
        });
      } else {
        toast({
          variant: 'destructive',
          title: 'Failed to clear conversation',
          description: 'An error occurred',
        });
      }
    } catch (err) {
      console.error('Error clearing conversation:', err);
      toast({
        variant: 'destructive',
        title: 'Failed to clear conversation',
        description: 'An unexpected error occurred',
      });
    }
  };

  return {
    messages,
    isLoading,
    isContextLoading,
    contextData,
    error,
    isOpen,
    messagesEndRef,
    sendMessage,
    toggleChatOpen,
    clearError,
    clearConversation,
  };
};
